import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import JSBI from 'jsbi'
import { parseUnits } from 'viem'

/**
 * Parses a CurrencyAmount from the passed string.
 * Returns the CurrencyAmount, or undefined if parsing fails.
 *
 * NOTE: This implementation converts the native `bigint` output of `viem/parseUnits`
 * back to a `JSBI` instance to satisfy the requirements of the external
 * `CurrencyAmount` library, which still expects `BigintIsh` to include `JSBI`.
 */
export default function tryParseCurrencyAmount<T extends Currency>(
  value?: string,
  currency?: T
): CurrencyAmount<T> | undefined {
  if (!value || !currency) {
    return undefined
  }

  try {
    const truncatedValue = truncateDecimals(value, currency.decimals)
    const typedValueParsedNative: bigint = parseUnits(truncatedValue, currency.decimals)

    if (typedValueParsedNative > 0n) {
      const typedValueParsedJSBI = JSBI.BigInt(typedValueParsedNative.toString())
      return CurrencyAmount.fromRawAmount(currency, typedValueParsedJSBI)
    }
  } catch (error) {
    console.debug(`Failed to parse input amount: "${value}"`, error)
  }
  return undefined
}

/**
 * Truncates the input string value to the specified number of decimals.
 * This prevents `parseUnits` from failing when the input has excessive precision.
 */
function truncateDecimals(value: string, decimals: number): string {
  const dotIndex = value.indexOf('.')
  if (dotIndex === -1) return value

  return value.slice(0, dotIndex + decimals + 1)
}
